import {
  AdminTopicsCategories,
  ColourCode,
  CommitmentLevel,
  ElementKind,
  EngagementLevel,
  EnglishSkill,
  EventCategory,
  EventEnglishLevel,
  EventPermissions,
  FaderLevel,
  Interest,
  LessonKind,
  MembershipPlans,
  SubscriptionStatus,
  TenureBucket,
  TopicStatus,
  UserBadge,
  UserBadgeType,
  ZoneLabel,
} from "../types/graphql";

export const DEBOUNCE_TIME = 300;

export const DEFAULT_OFFSET = 0;

export const PAGE_DEFAULT = 0;
export const ROWS_PER_PAGE_DEFAULT = 25;
export const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

export const EVENT_PAGE_DATE_FORMAT = "EEEE, MMM do yy";
export const EVENT_PAGE_TIME_FORMAT = "p";

export const LESSON_NAMES = new Map<LessonKind, string>([
  [LessonKind.Overview, "Overview"],
  [LessonKind.Vocabulary, "Vocabulary"],
  [LessonKind.DeepDive, "Deep Dive"],
  [LessonKind.Custom, "Custom"],
]);

export const TOPIC_STATUS = new Map<TopicStatus, string>([
  [TopicStatus.Current, "Current"],
  [TopicStatus.ComingSoon, "Coming Soon"],
  [TopicStatus.Inactive, "Inactive"],
]);

export const INTERESTS_NAMES = new Map<Interest, string>([
  [Interest.AnimalsAndWildlife, "Animals and wildlife"],
  [Interest.ArtsAndCrafts, "Arts and crafts"],
  [Interest.BeautyAndFashion, "Beauty and fashion"],
  [Interest.BusinessAndEntrepreneurship, "Business and entrepreneurship"],
  [Interest.CareersAndProfessionalDevelopment, "Careers and professional development"],
  [Interest.DifferentCultures, "Different cultures"],
  [Interest.Design, "Design"],
  [Interest.Economics, "Economics"],
  [Interest.EnvironmentalIssues, "Environmental issues"],
  [Interest.Equality, "Equality"],
  [Interest.FamilyAndMotherhood, "Family and motherhood"],
  [Interest.FeminismAndEmpowerment, "Feminism and empowerment"],
  [Interest.FoodAndCooking, "Food and cooking"],
  [Interest.Games, "Games"],
  [Interest.GoalsSettingAndSuccess, "Goals setting and success"],
  [Interest.HealthAndWellness, "Health and wellness"],
  [Interest.Hiking, "Hiking"],
  [Interest.History, "History"],
  [Interest.HomeAndGardening, "Home and gardening"],
  [Interest.Languages, "Languages"],
  [Interest.Ielts, "IELTS"],
  [Interest.Leadership, "Leadership"],
  [Interest.LivingAbroad, "Living abroad"],
  [Interest.LoveAndRelationships, "Love and relationships"],
  [Interest.MoviesAndTvShows, "Movies and TV shows"],
  [Interest.MusicAndDance, "Music and dance"],
  [Interest.NatureAndTheEnvironment, "Nature and environment"],
  [Interest.PersonalDevelopment, "Personal development"],
  [Interest.Photography, "Photography"],
  [Interest.Politics, "Politics"],
  [Interest.Psychology, "Psychology"],
  [Interest.Reading, "Reading"],
  [Interest.ScienceAndTechnology, "Science and technology"],
  [Interest.Spirituality, "Spirituality"],
  [Interest.Sports, "Sports"],
  [Interest.SustainableLiving, "Sustainable living"],
  [Interest.TheatreAndPerformance, "Theatre and performance"],
  [Interest.Travel, "Travel"],
  [Interest.VideoGames, "Video games"],
  [Interest.VolunteeringAndCharity, "Volunteering and charity"],
  [Interest.Writing, "Writing"],
]);

export const ENGLISH_SKILLS_NAMES = new Map<EnglishSkill, string>([
  [EnglishSkill.GivingAPresentation, "Giving a presentation"],
  [EnglishSkill.InterviewOrBeInterviewed, "Interview or be interviewed"],
  [EnglishSkill.IntroduceMyself, "Introduce myself"],
  [EnglishSkill.MeetNewPeople, "Meet new people"],
  [EnglishSkill.NegotiateAndDebate, "Negotiate and debate"],
  [EnglishSkill.PassAnEnglishExam, "Pass an English exam"],
  [EnglishSkill.PersonalNetworking, "Personal networking"],
  [EnglishSkill.Pronunciation, "Pronunciation"],
  [EnglishSkill.SpeakConfidentlyAtWork, "Speak confidently at work"],
  [EnglishSkill.ThinkInEnglish, "Think in English"],
  [EnglishSkill.UnderstandMoviesAndMusic, "Understand movies and music"],
  [EnglishSkill.UnderstandNativeSpeakers, "Understand native speakers"],
  [EnglishSkill.WriteCreatively, "Write creatively"],
  [EnglishSkill.WriteProfessionally, "Write professionally"],
]);

export enum BooleanOptions {
  True = "true",
  False = "false",
}

export const OPEN_TO_SPEAKING_PARTNERS = new Map<BooleanOptions, string>([
  [BooleanOptions.True, "Open to speaking partners"],
  [BooleanOptions.False, "Not open to speaking partners"],
]);

export const VERIFICATION_STATUSES = new Map<BooleanOptions, string>([
  [BooleanOptions.True, "Verified"],
  [BooleanOptions.False, "Not verified"],
]);

export const USER_BADGES = new Map<UserBadge, string>([
  [UserBadge.InactiveMember, "Inactive Member"],
  [UserBadge.LittleSister, "Little Sister"],
  [UserBadge.Member, "Member"],
  [UserBadge.BigSister, "Big Sister"],
  [UserBadge.EnglishCoach, "English Coach"],
  [UserBadge.CommunitySuccess, "Community Success"],
  [UserBadge.Founder, "Founder"],
  [UserBadge.MemberSupport, "Member Support"],
  [UserBadge.TechnicalSupport, "Technical Support"],
  [UserBadge.Guest, "Guest"],
]);

export const USER_BADGE_TYPES = new Map<UserBadgeType, string>([
  [UserBadgeType.ArticlesPublished_1, "Author"],
  [UserBadgeType.BigSister, "Big Sister"],
  [UserBadgeType.ScheduledConversations_1, "First Conversation"],
  [UserBadgeType.EventsParticipated_10, "10 Events"],
  [UserBadgeType.EventsParticipated_50, "50 Events"],
  [UserBadgeType.EventsParticipated_100, "100 Club"],
  [UserBadgeType.EventsParticipated_500, "500 Club"],
  [UserBadgeType.EventsParticipated_1000, "Conversation Queen"],
  [UserBadgeType.FoundingMember, "Founding Member"],
  [UserBadgeType.Hosted_1, "Host"],
  [UserBadgeType.Hosted_10, "Hosted 10 Events"],
  [UserBadgeType.Hosted_50, "Hosted 50 Events"],
  [UserBadgeType.Hosted_100, "Super Host"],
  [UserBadgeType.Hosted_500, "Ultra Host"],
  [UserBadgeType.LegendaryLady, "Legendary Lady"],
  [UserBadgeType.Membership_1, "One Year Member"],
  [UserBadgeType.Membership_2, "Two Year Member"],
  [UserBadgeType.Membership_3, "Three Year Member"],
  [UserBadgeType.Membership_4, "Four Year Member"],
  [UserBadgeType.Verified, "Verified"],
  [UserBadgeType.PerfectProfile, "Perfect Profile"],
  [UserBadgeType.HeyLadyWay, "Hey Lady! Way"],
  [UserBadgeType.CertifiedHost, "Certified Host"],
  [UserBadgeType.BroughtAFriend, "Brought a Friend"],
  [UserBadgeType.Ambassador, "Ambassador"],
  [UserBadgeType.PlatinumAmbassador, "Platinum Ambassador"],
  [UserBadgeType.DiamondAmbassador, "Diamond Ambassador"],
  [UserBadgeType.LocalHosted_1, "Local Hosted 1"],
  [UserBadgeType.LocalHosted_3, "Local Hosted 3"],
  [UserBadgeType.LocalHosted_10, "Local Hosted 10"],
  [UserBadgeType.LocalHosted_25, "Local Hosted 25"],
  [UserBadgeType.NmmEventAttended, "NMW Event Attended"],
]);

export const ASSIGNED_BADGES = [
  UserBadgeType.LegendaryLady,
  UserBadgeType.CertifiedHost,
  UserBadgeType.BigSister,
];

export const USER_PERMISSIONS = new Map<EventPermissions, string>([
  [EventPermissions.BigSister, "Big Sister"],
  [EventPermissions.HeyLadyAdmin, "Hey Lady Admin"],
  [EventPermissions.HeyLadyTeam, "Hey Lady Team"],
  [EventPermissions.InactiveMember, "Inactive Member"],
  [EventPermissions.OneToThree, "1 - 3 Months"],
  [EventPermissions.ThreeToTwelve, "3 - 12 Months"],
  [EventPermissions.MoreThanTwelveMonths, "More than 12 months"],
  [EventPermissions.ZeroToOne, "0 - 1 Months"],
]);

export const SUBSCRIPTION_TYPES = new Map<MembershipPlans, string>([
  [MembershipPlans.Monthly, "Monthly"],
  [MembershipPlans.Quarterly, "Quarterly"],
  [MembershipPlans.Annual, "Annual"],
  [MembershipPlans.FreeOneMonth, "Free One Month"],
  [MembershipPlans.ThreeMonthsOnly, "Three Months Only"],
  [MembershipPlans.SupporterOneMonth, "Supporter One Month"],
  [MembershipPlans.Social, "Basic Monthly"],
  [MembershipPlans.SocialQuarterly, "Basic Quarterly"],
  [MembershipPlans.SocialAnnual, "Basic Yearly"],
]);

export const SUBSCRIPTION_STATUSES = new Map<SubscriptionStatus, string>([
  [SubscriptionStatus.Active, "Active"],
  [SubscriptionStatus.Cancelled, "Cancelled"],
  [SubscriptionStatus.InTrial, "In Trial"],
  [SubscriptionStatus.NonRenewing, "Non Renewing"],
  [SubscriptionStatus.Paused, "Paused"],
]);

export const PERMISSION_BADGE_DEPENDENCY = new Map<EventPermissions, UserBadge[]>([
  [EventPermissions.BigSister, [UserBadge.BigSister]],
  [
    EventPermissions.HeyLadyAdmin,
    [
      UserBadge.EnglishCoach,
      UserBadge.CommunitySuccess,
      UserBadge.Founder,
      UserBadge.MemberSupport,
      UserBadge.TechnicalSupport,
    ],
  ],
  [EventPermissions.HeyLadyTeam, [UserBadge.EnglishCoach]],
  [EventPermissions.InactiveMember, [UserBadge.InactiveMember]],
  [EventPermissions.OneToThree, [UserBadge.Member, UserBadge.Guest]],
  [EventPermissions.ThreeToTwelve, [UserBadge.Member, UserBadge.Guest]],
  [EventPermissions.MoreThanTwelveMonths, [UserBadge.Member, UserBadge.Guest]],
  [EventPermissions.ZeroToOne, [UserBadge.LittleSister, UserBadge.Guest]],
]);

export const LESSON_PATHS = new Map<LessonKind, string>([
  [LessonKind.Overview, "overview"],
  [LessonKind.Vocabulary, "vocabulary"],
  [LessonKind.DeepDive, "deep-dive"],
]);

export const ELEMENT_TEMPLATES_TYPE = "TEMPLATES";

export const ELEMENT_TEMPLATES = new Map<ElementKind, any>([
  [ElementKind.Title, { kind: ElementKind.Title, value: undefined }],
  [ElementKind.Video, { kind: ElementKind.Video, value: undefined }],
  [ElementKind.Paragraph, { kind: ElementKind.Paragraph, value: undefined }],
  [ElementKind.Questions, { kind: ElementKind.Questions, value: [] }],
  [ElementKind.Divider, { kind: ElementKind.Divider, value: undefined }],
  [ElementKind.Spacing, { kind: ElementKind.Spacing, value: undefined }],
  [ElementKind.Image, { kind: ElementKind.Image, value: undefined }],
  [ElementKind.Attachment, { kind: ElementKind.Attachment, value: undefined }],
  [ElementKind.Links, { kind: ElementKind.Links, value: [] }],
  [ElementKind.Ideas, { kind: ElementKind.Ideas, value: [] }],
  [ElementKind.Layout, { kind: ElementKind.Layout, value: [] }],
  [ElementKind.Grid, { kind: ElementKind.Grid, value: [] }],
  [ElementKind.Button, { kind: ElementKind.Button, value: [] }],
  [ElementKind.Banner, { kind: ElementKind.Banner, value: [] }],
  [ElementKind.ImageAndButton, { kind: ElementKind.ImageAndButton, value: [] }],
  [ElementKind.Embedded, { kind: ElementKind.Embedded, value: [] }],
]);

export const EVENT_ENGLISH_LEVEL_NAMES = new Map<EventEnglishLevel, string>([
  [EventEnglishLevel.Intermediate, "Intermediate"],
  [EventEnglishLevel.Advanced, "Advanced"],
  [EventEnglishLevel.Everyone, "Everyone"],
]);

export const EVENT_CATEGORY_HLT_EVENTS = new Map<EventCategory, string>([
  [EventCategory.None, "None"],
  [EventCategory.Featured, "Featured"],
  [EventCategory.NewMember, "New Member"],
]);

export const EVENT_CATEGORY_MEMBER_EVENTS = new Map<EventCategory, string>([
  [EventCategory.None, "None"],
]);

export const VIDEO_SET_COLOURS = new Map<ColourCode, string>([
  [ColourCode.Pink, "#FF66A7"],
  [ColourCode.Orange, "#FF9266"],
  [ColourCode.Green, "#2B8740"],
  [ColourCode.Blue, "#32B3F7"],
  [ColourCode.Red, "#FF4653"],
]);

export enum TopicButtonType {
  Link = "Link",
  Video = "Pop-Up Video",
}

export enum TopicButtonAlignment {
  Left = "Left",
  Center = "Center",
  Right = "Right",
}

export enum GridColumns {
  TwoEqualColumns = "TwoEqualColumns",
  TreeEqualColumns = "TreeEqualColumns",
  OneThirdTwoThirds = "OneThirdTwoThirds",
  TwoThirdsOneThird = "TwoThirdsOneThird",
  OneFourthThreeFourths = "OneFourthThreeFourths",
  ThreeFourthsOneFourth = "ThreeFourthsOneFourth",
}

export enum MoveElement {
  UP = "Up",
  DOWN = "Down",
}

export enum MembersType {
  LittleSisters = "Little Sisters",
  Members = "Members",
}

export const SLIDER_AUDIENCE = new Map<EventPermissions[], MembersType>([
  [[EventPermissions.ZeroToOne], MembersType.LittleSisters],
  [
    [
      EventPermissions.OneToThree,
      EventPermissions.ThreeToTwelve,
      EventPermissions.MoreThanTwelveMonths,
      EventPermissions.BigSister,
      EventPermissions.HeyLadyTeam,
      EventPermissions.HeyLadyAdmin,
    ],
    MembersType.Members,
  ],
]);

export enum UrlFilterParams {
  /** Which population the Members page tabs show: current | past | all. */
  View = "view",
  /** Inside the Recovery Period Members view: "all" opts out of the recovery window. */
  Recovery = "recovery",
  /** Legacy param from the old Current/Past/Everyone toggle; read for old links. */
  Membership = "membership",
  Subscription = "subscriptionStatus",
  Plan = "plan",
  Kind = "kind",
  Location = "location",
  Interests = "interests",
  Skills = "englishSkills",
  Levels = "englishLevels",
  Open = "openToSpeakingPartners",
  Occupations = "occupations",
  Industries = "industries",
  Badges = "badges",
  VerificationStatus = "verificationStatus",
  Zones = "zones",
  Tenures = "tenures",
  MinEventsAttended = "minEventsAttended",
  StoryKeyword = "storyKeyword",
}

/**
 * Which members the page is about.
 *
 * Roughly 8 in 10 people in the database have already left, so showing everyone
 * by default buries the ~2,300 members you can actually do something about.
 * The page opens on Current and says so, rather than silently filtering.
 */
export enum Membership {
  Current = "current",
  Past = "past",
  All = "all",
}

/**
 * "Current" is everyone still on a live membership — including the ones on
 * their way out. Cancelling and paused members are exactly who the retention
 * and cancellation tools chase, so leaving them out would hide the work.
 */
export const CURRENT_MEMBER_STATUSES: SubscriptionStatus[] = [
  SubscriptionStatus.Active,
  SubscriptionStatus.InTrial,
  SubscriptionStatus.NonRenewing,
  SubscriptionStatus.Paused,
];

export const PAST_MEMBER_STATUSES: SubscriptionStatus[] = [SubscriptionStatus.Cancelled];

export const MEMBERSHIP_LABELS = new Map<Membership, string>([
  [Membership.Current, "Current members"],
  [Membership.Past, "Recovery Period Members"],
  [Membership.All, "Everyone"],
]);

/** The statuses a membership choice stands for. Everyone means "don't filter". */
export const membershipStatuses = (membership: Membership): SubscriptionStatus[] => {
  if (membership === Membership.Current) {
    return CURRENT_MEMBER_STATUSES;
  }
  if (membership === Membership.Past) {
    return PAST_MEMBER_STATUSES;
  }
  return [];
};

/**
 * Colour zones — the three-hour UTC band a member is actually around in.
 * Order matches the day, so don't sort these.
 */
export const ZONE_LABELS = new Map<ZoneLabel, string>([
  [ZoneLabel.Red, "RED · 00:00–02:59"],
  [ZoneLabel.Orange, "ORANGE · 03:00–05:59"],
  [ZoneLabel.Yellow, "YELLOW · 06:00–08:59"],
  [ZoneLabel.Green, "GREEN · 09:00–11:59"],
  [ZoneLabel.Blue, "BLUE · 12:00–14:59"],
  [ZoneLabel.Purple, "PURPLE · 15:00–17:59"],
  [ZoneLabel.Pink, "PINK · 18:00–20:59"],
  [ZoneLabel.Black, "BLACK · 21:00–23:59"],
]);

/** Zones in day order (RED 00:00 → BLACK 21:00). Don't sort. */
export const ZONE_ORDER: ZoneLabel[] = [
  ZoneLabel.Red,
  ZoneLabel.Orange,
  ZoneLabel.Yellow,
  ZoneLabel.Green,
  ZoneLabel.Blue,
  ZoneLabel.Purple,
  ZoneLabel.Pink,
  ZoneLabel.Black,
];

/** The colour each zone is named for, for the chips on the members table. */
export const ZONE_COLOURS = new Map<ZoneLabel, string>([
  [ZoneLabel.Red, "#c0392b"],
  [ZoneLabel.Orange, "#d35400"],
  [ZoneLabel.Yellow, "#c29d0b"],
  [ZoneLabel.Green, "#00583D"],
  [ZoneLabel.Blue, "#2471a3"],
  [ZoneLabel.Purple, "#7d3c98"],
  [ZoneLabel.Pink, "#b23a6a"],
  [ZoneLabel.Black, "#272727"],
]);

export const TENURE_LABELS = new Map<TenureBucket, string>([
  [TenureBucket.UpTo_1Month, "0 to 1 month"],
  [TenureBucket.OneTo_3Months, "1 to 3 months"],
  [TenureBucket.ThreeTo_6Months, "3 to 6 months"],
  [TenureBucket.SixTo_12Months, "6 to 12 months"],
  [TenureBucket.Over_12Months, "12+ months"],
]);

/** Friendly names for each fader (risk) level. */
export const FADER_LABELS = new Map<FaderLevel, string>([
  [FaderLevel.RecentlyEngaged, "Recently Engaged"],
  [FaderLevel.Detected, "Detected"],
  [FaderLevel.Dormant, "Dormant"],
  [FaderLevel.Critical, "Critical"],
  [FaderLevel.NewNeverAttended, "New Member (Never Attended)"],
  [FaderLevel.NewBelow_3Events, "New Member (Below 3 Events)"],
  [FaderLevel.New_3PlusEvents, "New Member (3+ Events)"],
  [FaderLevel.TrialNeverAttended, "Trial (Never Attended)"],
  [FaderLevel.TrialBelow_3Events, "Trial (Below 3 Events)"],
  [FaderLevel.Trial_3PlusEvents, "Trial (3+ Events)"],
  [FaderLevel.Paused, "Paused"],
  [FaderLevel.Cancelled, "Cancelled"],
]);

/**
 * Plain-language definitions of the invented filters, so the team knows what
 * they mean. Shown as info tooltips wherever the filter appears. Wording
 * follows the churn/engagement models these are computed from.
 */
export const FILTER_DEFINITIONS: Record<string, string> = {
  fader:
    "How close a member is to drifting away, based on how long since her last real conversation. Recently Engaged = seen in the last 2 weeks. Detected = 14+ days quiet (early warning). Dormant = 30+ days. Critical = 60+ days or never attended. Trial/New/Paused/Cancelled come from her membership status.",
  engagement:
    "How ACTIVE she's been over the last 12 weeks — total sessions and how many weeks she showed up. E1 Power User (every week, very active) down to E5 Never Engaged (barely any sessions). Unknown = too new to tell (under 2 weeks).",
  commitment:
    "How RELIABLE she is — a score from how often she logs in, the share of weeks she's active, and whether she's been on recently. Extremely → Barely Committed. Engagement is how much she does; commitment is how consistently.",
  zone: "The 3-hour part of the day she's usually around, as a colour — worked out from when she attends and logs in, else her time zone. Used to invite her to sessions at a time that suits her.",
  tenure:
    "How long she's been a paying member, counted in active (paid) days rather than calendar days — so a paused stretch doesn't inflate it.",
};

/** Friendly names for each engagement level. */
export const ENGAGEMENT_LABELS = new Map<EngagementLevel, string>([
  [EngagementLevel.E1PowerUser, "E1 – Power User"],
  [EngagementLevel.E2HighlyEngaged, "E2 – Highly Engaged"],
  [EngagementLevel.E3ModeratelyEngaged, "E3 – Moderately Engaged"],
  [EngagementLevel.E4LightlyEngaged, "E4 – Lightly Engaged"],
  [EngagementLevel.E5NeverEngaged, "E5 – Never Engaged"],
  [EngagementLevel.Unknown, "Unknown"],
]);

/** Friendly names for each commitment level. */
export const COMMITMENT_LABELS = new Map<CommitmentLevel, string>([
  [CommitmentLevel.ExtremelyCommitted, "Extremely Committed"],
  [CommitmentLevel.HighlyCommitted, "Highly Committed"],
  [CommitmentLevel.ModeratelyCommitted, "Moderately Committed"],
  [CommitmentLevel.LightlyCommitted, "Lightly Committed"],
  [CommitmentLevel.BarelyCommitted, "Barely Committed"],
]);

/** The at-risk segments the Online Now / retention outreach tools target. */
export const AT_RISK_SEGMENTS: FaderLevel[] = [
  FaderLevel.Detected,
  FaderLevel.Dormant,
  FaderLevel.Critical,
  FaderLevel.NewNeverAttended,
  FaderLevel.Paused,
];

/**
 * Which template group a segment uses. Detected / Dormant / Critical share the
 * "at risk" wording; new members and paused members get their own.
 */
export enum OutreachTemplateGroup {
  AtRisk = "at_risk",
  Never = "never",
  Paused = "paused",
}

export const SEGMENT_TEMPLATE_GROUP = new Map<FaderLevel, OutreachTemplateGroup>([
  [FaderLevel.Detected, OutreachTemplateGroup.AtRisk],
  [FaderLevel.Dormant, OutreachTemplateGroup.AtRisk],
  [FaderLevel.Critical, OutreachTemplateGroup.AtRisk],
  [FaderLevel.NewNeverAttended, OutreachTemplateGroup.Never],
  [FaderLevel.Paused, OutreachTemplateGroup.Paused],
]);

/**
 * Default message per template group, ported from the member tools' Online Now
 * templates. The send adds "Hi @Name!" itself, so these are just the body.
 */
export const ONLINE_NOW_TEMPLATES: Record<OutreachTemplateGroup, { label: string; text: string }> =
  {
    [OutreachTemplateGroup.AtRisk]: {
      label: "At risk (Detected / Dormant / Critical)",
      text: "Hey! I noticed you're online right now. It's been a little while since I've seen you around, so I wanted to say hi. 👋 How's everything going with you?",
    },
    [OutreachTemplateGroup.Never]: {
      label: "New member (never attended)",
      text: "Hey! I noticed you're online right now 😊 So glad you're here! I'd love to help you get started — would you like me to recommend an event for you? Is there anything I can help you with?",
    },
    [OutreachTemplateGroup.Paused]: {
      label: "Paused",
      text: "Hey! I noticed you're online right now so I thought I'd say hi! 👋 I know you've been on a break, so I'm here if you want some help getting back into a regular routine again — let me know how I can help!",
    },
  };

export const TOPIC_CATEGORIES = new Map<AdminTopicsCategories, string>([
  [AdminTopicsCategories.Standard, "Current"],
  [AdminTopicsCategories.Resource, "Resource"],
  [AdminTopicsCategories.GrammarLibrary, "Grammar Library"],
  [AdminTopicsCategories.TopicLibrary, "Topic Library"],
]);
